import { AbstractUserObjectDisplayPlugin } from 'app/components/map/ol/plugin/AbstractUserObjectDisplayPlugin';
import { LayerType, MapClickElement, MapElement } from 'app/components/map/util/MapUtils';
import { Airspace, FlightLevelBand, TimePeriod } from 'app/model/CORE';
import OLMap from 'app/components/map/ol/OLMap';
import Feature from 'ol/Feature';
import { StyleLike } from 'ol/style/Style';
import OLUtils from 'app/components/map/ol/OLUtils';
import * as ol from 'ol';
import MapConstants from 'app/components/map/MapConstants';
import * as geom from 'ol/geom';
import * as style from 'ol/style';
import OLMapStyles from 'app/components/map/ol/OLMapStyles';
import {
    MapLayer,
    MapLayerType,
    MapObjectType,
    WeatherLayerUtils,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import { MeteoForecast, TrafficVolumeMeteoForecast } from 'app/model/SOL49';
import { MapConvection } from 'app/components/map/CoreMap';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';

export const VALIDITY_PERIOD_PROPERTY_NAME: string = 'validityPeriod';

export class TvConvectionMapDisplayPlugin extends AbstractUserObjectDisplayPlugin {
    private readonly PLUGIN_LAYER_TYPE = LayerType.WEATHER;
    private readonly PLUGIN_LAYER_TYPE2 = MapLayerType.WEATHER;
    private readonly PLUGIN_MAP_ELEMENT = MapElement.CONVECTION;
    private readonly PLUGIN_MAP_OBJECT_TYPE = MapObjectType.CONVECTION;

    canBeDisplayed(layer: MapLayer, object: any, timePeriod: TimePeriod, flightLevelBand: FlightLevelBand): boolean {
        return (
            this.isMapElement(layer, this.PLUGIN_MAP_OBJECT_TYPE) &&
            this.PLUGIN_LAYER_TYPE2 === layer.type &&
            object['convection'] &&
            object['convection']['airspaceRef'] && //only supports metsafe
            object['tvAirspaceMap'] &&
            object['convection']['validityPeriod'] &&
            TimePeriodUtils.intersects(timePeriod, object['convection']['validityPeriod'] as TimePeriod) &&
            FlightLevelUtils.intersects(flightLevelBand, (object['convection'] as MeteoForecast)?.flightLevelBand) && //TODO rely on airspace instead?
            (layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL] as WeatherMinLevel)?.getLevel() <=
                (object['convection']['level'] as number)
        );
    }

    display(
        olMap: OLMap,
        object: any,
        mapLayer: MapLayer,
        timePeriod: TimePeriod,
        flightLevelBand: FlightLevelBand,
    ): Feature | Feature[] {
        const mac = object as MapConvection;
        const convection: TrafficVolumeMeteoForecast = mac.convection;
        const tvAirspaceDefinition: Airspace = mac.tvAirspaceMap[convection.id];
        if (!tvAirspaceDefinition) {
            console.warn('missing definition', convection.id, mac);
            return null;
        }
        let polygons;
        const airspaceOutline = olMap.getAirspaceOutline(tvAirspaceDefinition);
        if (airspaceOutline) {
            polygons = OLUtils.buildPolygons(airspaceOutline);
        } else {
            polygons = OLUtils.buildPolygons(tvAirspaceDefinition);
        }

        if (mapLayer.showLabel) {
            const features: ol.Feature[] = olMap.getPolygonArrayFeatureWithLabel(
                polygons,
                `${convection.airspaceRef.id}`,
            );
            features[1].set(MapConstants.ATTR_TIME_PERIOD, mac.convection.validityPeriod);
            features[1].set(MapConstants.ATTR_TV_NAME, mac.convection.id);
            features[1].set(MapConstants.ATTR_TYPE, MapConstants.TV_CONVECTION_TYPE);
            features[1].setStyle(this.getMapAirspaceConvectionStyle(mac));
            features[0].set(MapConstants.ATTR_MAP_LAYER_ID, mapLayer.id);
            this.applyStyle(features, mapLayer);
            return features;
        } else {
            const mp: geom.MultiPolygon = new geom.MultiPolygon(polygons);
            const feature = new ol.Feature(mp);
            feature.set(MapConstants.ATTR_TIME_PERIOD, mac.convection.validityPeriod);
            feature.set(MapConstants.ATTR_TV_NAME, mac.convection.id);
            feature.set(MapConstants.ATTR_TYPE, MapConstants.TV_CONVECTION_TYPE);
            feature.setStyle(this.getMapAirspaceConvectionStyle(mac));
            this.applyStyle([feature], mapLayer);
            return feature;
        }
    }

    getStyle(mapLayer: MapLayer): StyleLike {
        return null;
    }

    processClickEvent(features: Array<Feature>, clientXY: number[]): Array<MapClickElement> {
        return null;
    }

    processRightClickEvent(features: Array<Feature>, clientXY: number[]): Array<MapClickElement> {
        const clickElements: Array<MapClickElement> = [];
        for (const feature of features) {
            const type: string = feature.get(MapConstants.ATTR_TYPE);

            //sanity check
            if (type === MapConstants.TV_CONVECTION_TYPE) {
                const tvName: string = feature.get(MapConstants.ATTR_TV_NAME);
                const validityPeriod: TimePeriod = feature.get(MapConstants.ATTR_TIME_PERIOD);
                clickElements.push({
                    type: MapObjectType.CONVECTION,
                    subject: tvName,
                    layerId: feature.get(MapConstants.ATTR_MAP_LAYER_ID),
                    properties: {
                        [VALIDITY_PERIOD_PROPERTY_NAME]: validityPeriod,
                    },
                });
            }
        }
        return clickElements;
    }

    private getMapAirspaceConvectionStyle(input: MapConvection): style.Style {
        switch (input.convection.level) {
            case 0: {
                return OLMapStyles.CONVECTION_LEVEL_ZERO_STYLE.clone();
            }
            case 1: {
                return OLMapStyles.CONVECTION_LEVEL_ONE_STYLE.clone();
            }
            case 2: {
                return OLMapStyles.CONVECTION_LEVEL_TWO_STYLE.clone();
            }
            case 3: {
                return OLMapStyles.CONVECTION_LEVEL_THREE_STYLE.clone();
            }
            case 4: {
                return OLMapStyles.CONVECTION_LEVEL_FOUR_STYLE.clone();
            }
        }
        return null;
    }
}
